import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Search, PanelLeftClose } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export const PANEL_W = 256;
export const ACTIVE = "bg-white text-[hsl(var(--navy))] shadow-[0_1px_2px_hsl(var(--shadow-color)/0.06),0_0_0_1px_hsl(var(--shadow-color)/0.05)] hover:bg-white";

export function IconButton({ label, kbd, onClick, children, testId, className, active }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" onClick={onClick} aria-label={label} data-testid={testId} className={cn("flex h-8 w-8 items-center justify-center rounded-[9px] text-foreground/55 outline-none transition-colors hover:bg-black/[0.05] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40", active && "bg-black/[0.06] text-foreground", className)}>
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" sideOffset={6} className="flex items-center gap-2 rounded-lg px-2.5 py-1 text-[12px]">{label}{kbd ? <span className="kbd">{kbd}</span> : null}</TooltipContent>
    </Tooltip>
  );
}

/** Context panel frame: module title + actions on top, content below. Content decides its own scrolling. */
export function Panel({ title, meta, actions, children, onCollapse, onSearch, testId = "panel", className }) {
  return (
    <aside className={cn("flex h-full w-full min-w-0 shrink-0 flex-col bg-[hsl(var(--sidebar))]", className)} data-testid={testId}>
      <div className="flex h-[56px] shrink-0 items-center justify-between pl-4 pr-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 className="truncate font-display text-[15px] font-semibold tracking-[-0.01em]" data-testid={`${testId}-title`}>{title}</h2>
          {meta ? <span className="shrink-0 text-[12px] text-muted-foreground" data-testid={`${testId}-meta`}>{meta}</span> : null}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {actions}
          {onSearch ? <IconButton label="Søk" kbd="⌘K" onClick={onSearch} testId={`${testId}-search`}><Search className="h-[17px] w-[17px]" strokeWidth={1.9} /></IconButton> : null}
          {onCollapse ? <IconButton label="Skjul panel" kbd="⌘⇧S" onClick={onCollapse} testId={`${testId}-collapse`}><PanelLeftClose className="h-[17px] w-[17px]" strokeWidth={1.8} /></IconButton> : null}
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </aside>
  );
}

export function SectionLabel({ children, className, right }) {
  return (
    <div className={cn("flex items-center justify-between px-3 pb-1.5 pt-1 text-[12px] font-medium text-muted-foreground/70", className)}>
      <span>{children}</span>
      {right}
    </div>
  );
}

/** A nav row in the panel; `to` makes it a link, otherwise a button. Active = white card like ChatGPT. */
export function PanelRow({ to, onClick, icon: Icon, label, right, active: forced, end, testId, className, children, title }) {
  const { pathname, search } = useLocation();
  const active = forced ?? (to ? (end ? pathname === to : `${pathname}${search}` === to || pathname === to) : false);
  const cls = cn("group flex h-9 w-full items-center gap-2.5 rounded-[10px] px-2.5 text-left text-[13.5px] font-medium text-foreground/75 outline-none transition-[background-color,color,box-shadow] duration-150 hover:bg-black/[0.045] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40", active && ACTIVE, className);
  const inner = (
    <>
      {Icon ? <Icon className={cn("h-[17px] w-[17px] shrink-0 transition-colors", active ? "text-[hsl(var(--navy))]" : "text-foreground/50 group-hover:text-foreground")} strokeWidth={active ? 2.1 : 1.75} /> : null}
      {children || <span className="min-w-0 flex-1 truncate">{label}</span>}
      {right ? <span className="ml-auto shrink-0">{right}</span> : null}
    </>
  );
  if (to) return <NavLink to={to} end={end} className={cls} aria-current={active ? "page" : undefined} data-testid={testId} title={title}>{inner}</NavLink>;
  return <button type="button" onClick={onClick} className={cls} data-testid={testId} title={title}>{inner}</button>;
}

export function Count({ n, tone = "muted", testId }) {
  if (!n) return null;
  return <span className={cn("inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 font-mono text-[10.5px] font-semibold tabular-nums", tone === "brand" ? "bg-primary text-white" : "bg-black/[0.06] text-foreground/60")} data-testid={testId}>{n > 99 ? "99+" : n}</span>;
}
